import type {
  GridOptions,
  GridPreDestroyedEvent,
  GridReadyEvent
} from "ag-grid-enterprise";
import type { AgGridReactProps } from "ag-grid-react";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo } from "react";
import { useActiveGrid } from "../activeGrid/ActiveGridContext";
import { ErrorBoundary } from "../errorBoundary/ErrorBoundary";
import { theme } from "./DataTableTheme";
import {
  GridRowDescriptor,
  useGridFeedback,
  withGridFeedbackMenu
} from "./GridFeedbackContext";
import { copyNumbersUnformatted } from "./utils/clipboard";
import { PERCENT_UNIT_FILTER_PRECISION } from "./utils/getColumnFiltersByType";
import {
  decimalFormatter,
  percentFormatter,
  percentUnitFormatter
} from "./utils/valueFormatters";
import {
  percentScaledValueParser,
  percentUnitValueParser
} from "./utils/valueParsers";

export type DataTableProps = AgGridReactProps & {
  ref?: React.Ref<AgGridReact>;
  /**
   * Remove borders from cells and rows
   */
  noBorders?: boolean;
  /**
   * Domain identity extractor for feedback's "Report this value": the keys
   * of a row that identify it to a human (ids, not KPIs). Optional — the
   * generic capture (column, value, scalar row snapshot) works without it.
   */
  feedbackDescribeRow?: GridRowDescriptor;
};

/** AG Grid's built-in aggregation functions. */
const BUILT_IN_AGG_FUNCS = [
  "avg",
  "count",
  "first",
  "last",
  "max",
  "min",
  "sum"
];

export function DataTable(props: DataTableProps) {
  // Allow consumers to override defaults via props.gridOptions.
  // `getContextMenuItems` is destructured out of the direct props too: AG
  // Grid gives a direct prop precedence over gridOptions, so leaving it in
  // `rest` would bypass the feedback wrapper below and silently drop
  // "Report this value" for consumers that pass it as a prop.
  const {
    gridOptions,
    noBorders,
    onGridReady,
    onGridPreDestroyed,
    feedbackDescribeRow,
    getContextMenuItems,
    ...rest
  } = props;

  const { register, unregister } = useActiveGrid();
  // Each handler may arrive as a top-level prop or inside gridOptions; wrap
  // whichever so registration doesn't shadow the caller's handler.
  const callerOnGridReady = onGridReady ?? gridOptions?.onGridReady;
  const callerOnGridPreDestroyed =
    onGridPreDestroyed ?? gridOptions?.onGridPreDestroyed;
  const handleGridReady = useCallback(
    (event: GridReadyEvent) => {
      register(event.api);
      callerOnGridReady?.(event);
    },
    [register, callerOnGridReady]
  );
  // Fires on every teardown — unmount, StrictMode, the error boundary below —
  // so a destroyed grid never stays registered.
  const handleGridPreDestroyed = useCallback(
    (event: GridPreDestroyedEvent) => {
      unregister(event.api);
      callerOnGridPreDestroyed?.(event);
    },
    [unregister, callerOnGridPreDestroyed]
  );

  // When the app wired the feedback integration (FeedbackProvider does),
  // every DataTable's context menu grows "Report this value" — no per-grid
  // opt-in. Null integration (tests, storybook) keeps today's behavior.
  const gridFeedback = useGridFeedback();

  // Lists the aggregation menu alphabetically rather than by AG Grid's own
  // ordering. A grid passing custom `aggFuncs` gets them merged in, so this
  // never hides an aggregation the grid actually registered.
  const allowedAggFuncs = useMemo(
    () =>
      [
        ...BUILT_IN_AGG_FUNCS,
        ...Object.keys(gridOptions?.aggFuncs ?? {})
      ].sort(),
    [gridOptions?.aggFuncs]
  );

  const defaultGridOptions: GridOptions = useMemo(
    () => ({
      // Layout & sizing
      headerHeight: 36,
      // Lets the browser skip rendering off-screen grids. Not the deprecated
      // `suppressContentVisibilityAuto` — that alias applies at a different
      // point in the lifecycle and stops the overlays rendering.
      enableContentVisibilityAuto: true,
      // Row selection
      cellSelection: true,
      rowSelection: {
        mode: "singleRow",
        enableClickSelection: true,
        enableSelectionWithoutKeys: true,
        checkboxes: false,
        headerCheckbox: false
      },

      // Column defaults
      suppressAggFuncInHeader: true,
      groupLockGroupColumns: -1,
      defaultColDef: {
        resizable: true,
        sortable: true,
        flex: 1,
        minWidth: 85,
        allowedAggFuncs
      },
      columnTypes: {
        string: {
          cellStyle: { textAlign: "left" }
        },
        float: {
          cellStyle: { textAlign: "right" },
          valueFormatter: (params) => decimalFormatter(params, 2)
        },
        image: {
          cellStyle: { display: "flex", justifyContent: "center" }
        },
        integer: {
          cellStyle: { textAlign: "right" },
          valueFormatter: (params) => decimalFormatter(params, 0)
        },
        percent: {
          cellStyle: { textAlign: "right" },
          valueFormatter: (params) => percentFormatter(params, 2),
          // The editor seeds the label, so a percent cell reads and writes the
          // one unit it shows — a typed 10 is 10 %, never 1000 %.
          cellEditor: "agTextCellEditor",
          cellEditorParams: { useFormatter: true },
          valueParser: percentScaledValueParser
        },
        percentUnit: {
          cellStyle: { textAlign: "right" },
          // PERCENT_UNIT_FILTER_PRECISION is documented as the finest any
          // percentUnit column displays — a coarser default here would
          // truncate a fractional value on copy/paste round-trip.
          valueFormatter: (params) =>
            percentUnitFormatter(params, PERCENT_UNIT_FILTER_PRECISION),
          valueParser: percentUnitValueParser
        },
        array: { cellStyle: { textAlign: "left" } }
      },

      // Clipboard & data handling
      suppressClipboardPaste: false,
      // Excel ends every copy with a newline; without this AG Grid pastes it
      // as a blank into the row below the block, clearing that cell.
      suppressLastEmptyLineOnPaste: true,
      processDataFromClipboard: (params) => params.data,
      processCellForClipboard: copyNumbersUnformatted,

      // UX
      tooltipShowDelay: 250,

      // Sorting & Excel export
      excelStyles: [
        { id: "integerType", numberFormat: { format: "0" } },
        { id: "numberType", numberFormat: { format: "0.00" } }
      ]
    }),
    [allowedAggFuncs]
  );

  // Merge with incoming gridOptions so consumers can override, then let the
  // feedback integration append its context-menu item on top of whatever
  // menu the consumer configured. The direct `getContextMenuItems` prop wins
  // over gridOptions' — the precedence AG Grid itself applies — and both
  // routes flow through the feedback wrapper.
  const mergedGridOptions = useMemo<GridOptions>(
    () =>
      withGridFeedbackMenu(
        {
          ...defaultGridOptions,
          ...gridOptions,
          ...(getContextMenuItems ? { getContextMenuItems } : {})
        },
        gridFeedback,
        feedbackDescribeRow
      ) as GridOptions,
    [
      defaultGridOptions,
      gridOptions,
      getContextMenuItems,
      gridFeedback,
      feedbackDescribeRow
    ]
  );

  const themeOverrides = noBorders
    ? {
        borderColor: "transparent",
        rowBorder: false,
        headerColumnBorder: false,
        wrapperBorder: false
      }
    : {};

  return (
    <ErrorBoundary level="component">
      <AgGridReact
        theme={theme.withParams(themeOverrides)}
        gridOptions={mergedGridOptions}
        {...rest}
        onGridReady={handleGridReady}
        onGridPreDestroyed={handleGridPreDestroyed}
      />
    </ErrorBoundary>
  );
}
